import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { STAGE_ORDER } from '../../utils/formatters';

function advancedBeyondPIF(deal) {
  return STAGE_ORDER.indexOf(deal.current_stage) > 0;
}

// Default bands in base currency units
const DEFAULT_BANDS = [
  { label: '< 1M',    min: 0,         max: 999999 },
  { label: '1M – 5M', min: 1000000,   max: 4999999 },
  { label: '5M – 10M',min: 5000000,   max: 9999999 },
  { label: '10M+',    min: 10000000,  max: Infinity },
];

export default function TicketSizeAnalysisView({ deals, fund }) {
  const currency = fund?.base_currency || '';
  const currencyPrefix = currency ? `${currency} ` : '';

  const bands = DEFAULT_BANDS.map(band => {
    const bDeals = deals.filter(d => {
      const amt = d.requested_amount || 0;
      return amt >= band.min && amt <= band.max;
    });
    const scores = bDeals.filter(d => d.pif_score != null).map(d => d.pif_score);
    const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : '—';
    const advanced = bDeals.filter(advancedBeyondPIF).length;
    const crossCurrency = bDeals.filter(d => d.requested_currency !== currency).map(d => d.requested_currency);
    const uniqueCross = [...new Set(crossCurrency)];
    return {
      label: `${currencyPrefix}${band.label}`,
      count: bDeals.length,
      avgScore,
      advRate: bDeals.length > 0 ? Math.round((advanced / bDeals.length) * 100) : 0,
      crossCurrencyNote: uniqueCross.length > 0 ? `Includes ${uniqueCross.join(', ')} deals` : null,
    };
  }).filter(b => b.count > 0);

  if (!bands.length) {
    return <p className="text-muted" style={{ fontSize: 13 }}>No deal amount data available.</p>;
  }

  return (
    <div>
      <div className="card mb-4">
        <div className="card-header"><h2>Deal Count by Ticket Size</h2></div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bands} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" name="Deals" radius={[4, 4, 0, 0]}>
                {bands.map((_, i) => <Cell key={i} fill="var(--color-accent)" fillOpacity={0.8} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h2>Ticket Size Breakdown</h2></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Band</th>
                <th>Deals</th>
                <th>Avg PIF Score</th>
                <th>Advancement Rate</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {bands.map(b => (
                <tr key={b.label}>
                  <td style={{ fontWeight: 500 }}>{b.label}</td>
                  <td>{b.count}</td>
                  <td>{b.avgScore}</td>
                  <td>{b.advRate}%</td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {b.crossCurrencyNote || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '8px 16px 12px' }}>
          Deals in non-fund currencies are placed in bands by their face value in original currency.
        </p>
      </div>
    </div>
  );
}
